import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, Button, ClickAwayListener, Popper, Typography } from "@mui/material";
import type { AutocompleteInputChangeReason } from "@mui/material/Autocomplete";
import type { AutocompleteCloseReason } from "@mui/material/useAutocomplete";
import { type FC, type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type Department, useAuth } from "@/contexts/AuthProvider";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { SURFACE } from "@/theme/tokens";
import { buildSwitcherRows, departmentNameInFacility, groupDepartmentsByFacility, type SwitcherRow, switcherRowLabel } from "@/utils/facilityDepartments";
import { DepartmentSwitcherList, type SwitcherRowOption } from "./DepartmentSwitcherList";
import { headerStyles } from "./utils";

const POPPER_WIDTH = { xs: "100%", sm: 400 } as const;
const NO_EXPANDED_FACILITIES: ReadonlySet<string> = new Set();

const rowKey = (row: SwitcherRow): string =>
	row.kind === "facility" ? `facility-${row.group.facility.facility_id}` : `department-${row.department.department_id}`;

type Props = {
	departments: Department[];
	onChange: (department: Department) => void | Promise<void>;
	isBedSide?: boolean;
};
export const DepartmentSwitcher: FC<Props> = ({ departments, onChange, isBedSide = false }) => {
	const { t } = useTranslation();
	const mainDepartmentLabel = t("header.facilitySwitcher.mainDepartment");
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();

	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [expandedFacilityIds, setExpandedFacilityIds] = useState<ReadonlySet<string>>(NO_EXPANDED_FACILITIES);
	const anchorRef = useRef<HTMLButtonElement>(null);
	const popperContentRef = useRef<HTMLDivElement>(null);
	// Picking a facility row expands it; the close MUI fires right after that pick must not close the list.
	const keepOpenAfterPickRef = useRef(false);

	useEffect(() => {
		if (!open) {
			return;
		}

		const frameID = window.requestAnimationFrame(() => {
			popperContentRef.current?.querySelector<HTMLInputElement>("input")?.focus();
		});
		return () => {
			window.cancelAnimationFrame(frameID);
		};
	}, [open]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				event.stopPropagation();
				setOpen(false);
			}
		};
		window.addEventListener("keydown", onKeyDown, true);
		return () => {
			window.removeEventListener("keydown", onKeyDown, true);
		};
	}, [open]);

	const hasMultiple = departments.length > 1;
	const groups = useMemo(() => groupDepartmentsByFacility(departments, userInfo.facilities), [departments, userInfo.facilities]);
	const chosenGroup = useMemo(
		() => groups.find((group) => group.departments.some((department) => department.department_id === chosenDepartment.department_id)),
		[groups, chosenDepartment.department_id],
	);
	const options = useMemo<SwitcherRowOption[]>(
		() =>
			buildSwitcherRows(groups, expandedFacilityIds, query, mainDepartmentLabel).map((row) => ({
				label: switcherRowLabel(row, mainDepartmentLabel),
				value: row,
				key: rowKey(row),
			})),
		[groups, expandedFacilityIds, query, mainDepartmentLabel],
	);
	// `undefined` (not in `options`) would flip the Autocomplete to uncontrolled; `null` keeps it controlled when the chosen facility is collapsed.
	const selectedValue = useMemo<SwitcherRowOption | null>(
		() => options.find(({ value: row }) => row.kind === "department" && row.department.department_id === chosenDepartment.department_id) ?? null,
		[options, chosenDepartment.department_id],
	);
	const groupBy = new Set(groups.map((group) => group.network)).size > 1 ? (option: SwitcherRowOption) => option.value.group.network : undefined;

	const chosenName = chosenDepartment.display_name || t("Department");
	const triggerLabel =
		chosenGroup && chosenGroup.departments.length > 1
			? t("header.facilitySwitcher.facilityAndDepartment", {
					facility: chosenGroup.facility.name,
					department: departmentNameInFacility(chosenDepartment.display_name, chosenGroup.facility.name, mainDepartmentLabel),
				})
			: chosenName;

	const handleTriggerClick = useCallback((): void => {
		if (!hasMultiple) {
			return;
		}
		setQuery("");
		setExpandedFacilityIds(chosenGroup ? new Set([chosenGroup.facility.facility_id]) : NO_EXPANDED_FACILITIES);
		setOpen((prev) => !prev);
	}, [hasMultiple, chosenGroup]);

	const handleClose = useCallback((): void => {
		setOpen(false);
	}, []);

	const handleChange = useCallback(
		async (_event: SyntheticEvent, option: SwitcherRowOption | null): Promise<void> => {
			if (!option) {
				return;
			}

			const row = option.value;
			if (row.kind === "facility") {
				keepOpenAfterPickRef.current = true;
				setExpandedFacilityIds((previous) => {
					const next = new Set(previous);
					if (!next.delete(row.group.facility.facility_id)) next.add(row.group.facility.facility_id);
					return next;
				});
				return;
			}

			try {
				await onChange(row.department);
			} catch (e) {
				console.error(e);
			}
			setOpen(false);
		},
		[onChange],
	);

	const handleOpenStateChange = useCallback(
		(_event: SyntheticEvent, reason: AutocompleteCloseReason | undefined, isOpen: boolean): void => {
			if (isOpen || reason !== "selectOption") return;
			if (keepOpenAfterPickRef.current) {
				keepOpenAfterPickRef.current = false;
				return;
			}
			handleClose();
		},
		[handleClose],
	);

	// A pick resets the input to the picked row's label; only what the user types is the search.
	const handleInputChange = useCallback((_event: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason): void => {
		if (reason === "input" || reason === "clear") setQuery(value);
	}, []);

	const triggerSx = isBedSide ? headerStyles.departmentButtonTextBedSide : headerStyles.departmentButtonText;

	// No `data-annie-target` on this branch: it is a label, not a switcher, and the walkthrough step on
	// that anchor says "switch between them". Dropping the anchor is the supported way to drop a step for
	// the users it doesn't apply to — WalkthroughController runs only the steps whose anchors are visible.
	if (!hasMultiple) {
		return (
			<Box data-testid="header-open-departments" sx={headerStyles.departmentButton}>
				<Typography sx={triggerSx}>{chosenName}</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Button
				ref={anchorRef}
				data-testid={"header-open-departments"}
				// Only the branch that can actually be clicked carries the anchor.
				data-annie-target="department-switcher"
				color="inherit"
				onClick={handleTriggerClick}
				endIcon={<KeyboardArrowDownIcon />}
				sx={headerStyles.departmentButton}
				aria-haspopup="listbox"
				aria-expanded={open}
			>
				<Typography sx={triggerSx}>{triggerLabel}</Typography>
			</Button>
			<Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" sx={{ zIndex: (theme) => theme.zIndex.modal, width: POPPER_WIDTH }}>
				<ClickAwayListener onClickAway={handleClose}>
					<Box ref={popperContentRef} sx={{ backgroundColor: SURFACE, borderRadius: 1, boxShadow: 3, overflow: "hidden", width: "100%" }}>
						<DepartmentSwitcherList
							options={options}
							selectedValue={selectedValue}
							groupBy={groupBy}
							chosenFacilityId={chosenGroup?.facility.facility_id}
							query={query}
							onInputChange={handleInputChange}
							onChange={handleChange}
							onOpenStateChange={handleOpenStateChange}
						/>
					</Box>
				</ClickAwayListener>
			</Popper>
		</Box>
	);
};
